import { useEffect, useState } from 'react'
import {
    Button,
    Input,
    Box,
    Text,
    Card,
    CardBody,
    Heading,
    Divider,
    VStack,
    HStack,
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { generalGetAPI } from '../../services/API/general.api'
import API_ENDPOINTS from '../../services/API/apiEndpoints.constants'
import { formatNumber } from '../../utils/localization/culture.utils'

function InsuranceOfferPage() {
    const [products, setProducts] = useState<{ id: number; name: string }[]>([])
    interface Insurance {
        baseInsurancePriceSteps: {
            maxValue: number
            id: number
            premiumPrice: number
            deductiblePrice: number
            netPrice: number
            note: string
        }[]
        baseInsuranceNumber: string
        description: string
        productInsurances: { productId: number }[]
    }

    const [insurances, setInsurances] = useState<Insurance[]>([])
    const [selectedProduct, setSelectedProduct] = useState<{
        id: number
        name: string
    } | null>(null)
    const [assetPrice, setAssetPrice] = useState('')
    const [matchingInsurances, setMatchingInsurances] = useState<
        {
            insurance: Insurance
            step: {
                maxValue: number
                id: number
                premiumPrice: number
                deductiblePrice: number
                netPrice: number
                note: string
            }
        }[]
    >([])

    useEffect(() => {
        async function fetchData() {
            const productResponse = await generalGetAPI(API_ENDPOINTS.product)
            const insuranceResponse = await generalGetAPI(
                API_ENDPOINTS.insurance
            )
            if (productResponse.isOk && insuranceResponse.isOk) {
                const insuredProductIds: Set<number> = new Set(
                    (insuranceResponse.data as Insurance[]).flatMap(
                        (insurance: Insurance) =>
                            insurance.productInsurances.map(
                                (p: { productId: number }) => p.productId
                            )
                    )
                )
                setProducts(
                    (
                        productResponse.data as { id: number; name: string }[]
                    ).filter((product) => insuredProductIds.has(product.id))
                )
                setInsurances(insuranceResponse.data)
            }
        }
        fetchData()
    }, [])

    const handleSelectProduct = (product: { id: number; name: string }) => {
        setSelectedProduct(product)
        setMatchingInsurances([]) // Reset insurances when selecting a new product
    }

    const handlePriceChange = (e: any) => {
        setAssetPrice(e.target.value)
    }

    const findMatchingInsurances = () => {
        if (!selectedProduct || !assetPrice) return

        const newMatchingInsurances = insurances.flatMap((insurance) => {
            if (
                insurance.productInsurances.some(
                    (p) => p.productId === selectedProduct.id
                )
            ) {
                const validSteps = insurance.baseInsurancePriceSteps.filter(
                    (step) => parseFloat(assetPrice) <= step.maxValue
                )
                const step =
                    validSteps.length > 0
                        ? validSteps.reduce((prev, curr) =>
                              prev.maxValue < curr.maxValue ? prev : curr
                          )
                        : null // Select the lowest maxValue
                return step ? [{ insurance, step }] : []
            }
            return []
        })
        setMatchingInsurances(newMatchingInsurances)
    }

    const translate = useTranslation().t

    return (
        <Box p={6} maxW="800px" mx="auto">
            <VStack spacing={4} align="stretch">
                <Heading size="lg" textAlign="center">
                    {translate('insuranceHeadline')}
                </Heading>
                <HStack spacing={4} wrap="wrap" justify="center">
                    {products.map((product) => (
                        <Button
                            key={product.id}
                            onClick={() => handleSelectProduct(product)}
                            colorScheme="blue"
                            variant={
                                selectedProduct?.id === product.id
                                    ? 'solid'
                                    : 'outline'
                            }
                        >
                            {product.name}
                        </Button>
                    ))}
                </HStack>

                {selectedProduct && (
                    <Box>
                        <Text fontWeight="bold" fontSize="lg">
                            {translate('price')}: {selectedProduct.name}
                        </Text>
                        <Input
                            type="number"
                            value={assetPrice}
                            onChange={handlePriceChange}
                            onBlur={findMatchingInsurances}
                            size="lg"
                        />
                    </Box>
                )}

                {matchingInsurances.length > 0 && (
                    <VStack spacing={4} align="stretch">
                        {matchingInsurances.map(
                            ({ insurance, step }, index) => (
                                <Card
                                    key={index}
                                    borderRadius="lg"
                                    shadow="md"
                                    p={4}
                                >
                                    <CardBody>
                                        <Heading size="md">
                                            {insurance.description}
                                        </Heading>
                                        <Divider my={2} />
                                        <Text>
                                            <strong>
                                                {translate('insuranceNumber')}:
                                            </strong>{' '}
                                            {insurance.baseInsuranceNumber}
                                        </Text>
                                        <Text>
                                            <strong>
                                                {translate('maxValue')}
                                            </strong>{' '}
                                            {formatNumber(step.maxValue)}
                                        </Text>
                                        <Text>
                                            <strong>
                                                {translate('premiumPrice')}
                                            </strong>{' '}
                                            {formatNumber(step.premiumPrice)}
                                        </Text>
                                        <Text>
                                            <strong>
                                                {translate('deductiblePrice')}
                                            </strong>{' '}
                                            {formatNumber(step.deductiblePrice)}
                                        </Text>
                                        <Text>
                                            <strong>
                                                {translate('netPrice')}
                                            </strong>{' '}
                                            {formatNumber(step.netPrice)}
                                        </Text>
                                        {step.note && (
                                            <Text color="gray.500">
                                                <strong>
                                                    {translate('note')}
                                                </strong>{' '}
                                                {step.note}
                                            </Text>
                                        )}
                                    </CardBody>
                                </Card>
                            )
                        )}
                    </VStack>
                )}
            </VStack>
        </Box>
    )
}

export default InsuranceOfferPage
